import React, { useEffect, useImperativeHandle, useMemo, useRef, forwardRef } from 'react';
import { useEditor, EditorContent, NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import LetterPlaceholderExtension from './LetterPlaceholderExtension.js';
import LetterQrBlockExtension from './LetterQrBlockExtension.js';
import LetterAlignmentExtension from './LetterAlignmentExtension.js';
import LetterFontExtension, { LETTER_TEMPLATE_FONT_CSS } from './LetterFontExtension.js';
import LetterFontSizeExtension, {
  LETTER_TEMPLATE_DEFAULT_FONT_SIZE_PT,
  LETTER_TEMPLATE_FONT_SIZES,
} from './LetterFontSizeExtension.js';
import LetterImageExtension, {
  LETTER_TEMPLATE_IMAGE_ALT_MAX,
  LETTER_TEMPLATE_IMAGE_WIDTH_MAX,
  LETTER_TEMPLATE_IMAGE_WIDTH_MIN,
} from './LetterImageExtension.js';
import {
  countLetterTemplateImageNodes,
  countLetterTemplateQrBlocks,
  letterTemplateDocumentsEqual,
} from './letterTemplateDocument.js';
import LetterTemplateToolbar from './LetterTemplateToolbar.jsx';
import { letterTemplatePlaceholderLabel } from '../adminProspectLetterTemplateDisplay.js';

function LetterPlaceholderNodeView({ node, selected, editor }) {
  const key = node.attrs.key;
  const labelFn = editor?.storage?.placeholderLabelFn;
  const label = typeof labelFn === 'function' ? labelFn(key) : key;
  return (
    <NodeViewWrapper as="span" className="inline">
      <span
        className={`letter-placeholder-chip inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${
          selected
            ? 'border-indigo-400 bg-indigo-50 text-indigo-900'
            : 'border-slate-300 bg-slate-100 text-slate-800'
        }`}
        data-placeholder-key={key}
        contentEditable={false}
      >
        [ {label} ]
      </span>
    </NodeViewWrapper>
  );
}

function LetterQrBlockNodeView({ node, selected, editor, getPos, deleteNode }) {
  const align = node.attrs.align || 'center';
  const sharedNote = editor?.storage?.bilingualSharedQrNote;
  const sharedNoteText = editor?.storage?.bilingualSharedQrNoteText;
  return (
    <NodeViewWrapper className="my-4" style={{ textAlign: align }}>
      <div
        className={`mx-auto max-w-xs rounded-lg border-2 border-dashed p-6 text-center ${
          selected ? 'border-indigo-400 bg-indigo-50' : 'border-slate-300 bg-slate-50'
        }`}
        contentEditable={false}
      >
        <p className="text-sm font-semibold tracking-wide text-slate-600">
          {editor?.storage?.letterQrLabel || 'QR'}
        </p>
        {sharedNote && sharedNoteText ? (
          <p className="mt-2 text-xs text-slate-600">{sharedNoteText}</p>
        ) : null}
        {editor?.isEditable ? (
          <button
            type="button"
            className="mt-2 text-xs text-rose-700 underline hover:text-rose-900"
            onClick={() => {
              if (!editor?.isEditable) return;
              if (typeof deleteNode === 'function') deleteNode();
              else if (typeof getPos === 'function') {
                const pos = getPos();
                if (typeof pos === 'number') {
                  editor.chain().focus().deleteRange({ from: pos, to: pos + node.nodeSize }).run();
                }
              }
            }}
          >
            {editor?.storage?.letterQrDeleteLabel || 'Delete'}
          </button>
        ) : null}
      </div>
    </NodeViewWrapper>
  );
}

function LetterImageNodeView({ node, selected, editor, updateAttributes, deleteNode, getPos }) {
  const { assetId, widthPct, align, alt } = node.attrs;
  const previewFn = editor?.storage?.getImagePreviewUrl;
  const previewUrl = typeof previewFn === 'function' ? previewFn(assetId) : null;
  const t = editor?.storage?.letterImageT;
  const editable = editor?.isEditable;

  const handleDelete = () => {
    if (!editable) return;
    if (typeof deleteNode === 'function') deleteNode();
    else if (typeof getPos === 'function') {
      const pos = getPos();
      if (typeof pos === 'number') {
        editor.chain().focus().deleteRange({ from: pos, to: pos + node.nodeSize }).run();
      }
    }
  };

  return (
    <NodeViewWrapper className="letter-image-node my-4 block w-full">
      <div
        className="letter-image-print-row w-full"
        style={{ textAlign: align || 'center' }}
        contentEditable={false}
      >
        <div
          className={`inline-block rounded border p-2 ${
            selected ? 'border-indigo-400 bg-indigo-50' : 'border-slate-200 bg-white'
          }`}
          style={{ width: `${widthPct || 50}%`, maxWidth: '100%' }}
        >
          {previewUrl ? (
            <img
              src={previewUrl}
              alt={alt || ''}
              className="mx-auto block max-w-full"
              onError={() => {
                const retry = editor?.storage?.retryImagePreview;
                if (typeof retry === 'function') retry(assetId);
              }}
            />
          ) : (
            <div className="flex min-h-[80px] items-center justify-center bg-slate-100 text-xs text-slate-500">
              {t?.('admin.prospection.letterTemplate.image.previewUnavailable') || 'Preview unavailable'}
            </div>
          )}
        </div>
      </div>
      {editable ? (
        <div className="letter-image-controls mt-2 w-full max-w-full rounded border border-slate-200 bg-slate-50 p-3 text-left text-xs sm:mx-auto sm:w-[280px] sm:max-w-[300px]">
          <label className="flex flex-wrap items-center gap-2">
            <span className="w-16 shrink-0">{t?.('admin.prospection.letterTemplate.image.width') || 'Width'}</span>
            <input
              type="range"
              min={LETTER_TEMPLATE_IMAGE_WIDTH_MIN}
              max={LETTER_TEMPLATE_IMAGE_WIDTH_MAX}
              step={5}
              value={widthPct || 50}
              onChange={(e) => updateAttributes({ widthPct: Number(e.target.value) })}
              className="min-w-0 flex-1"
            />
            <span className="w-10 shrink-0 text-right">{widthPct || 50}%</span>
          </label>
          <label className="mt-2 flex flex-wrap items-center gap-2">
            <span className="w-16 shrink-0">{t?.('admin.prospection.letterTemplate.image.align') || 'Align'}</span>
            <select
              value={align || 'center'}
              onChange={(e) => updateAttributes({ align: e.target.value })}
              className="min-w-0 flex-1 rounded border border-slate-300 px-1 py-0.5"
            >
              <option value="left">{t?.('admin.prospection.letterTemplate.toolbar.alignLeft') || 'Left'}</option>
              <option value="center">{t?.('admin.prospection.letterTemplate.toolbar.alignCenter') || 'Center'}</option>
              <option value="right">{t?.('admin.prospection.letterTemplate.toolbar.alignRight') || 'Right'}</option>
            </select>
          </label>
          <label className="mt-2 block">
            <span>{t?.('admin.prospection.letterTemplate.image.alt') || 'Alt'}</span>
            <input
              type="text"
              value={alt || ''}
              maxLength={LETTER_TEMPLATE_IMAGE_ALT_MAX}
              onChange={(e) => updateAttributes({ alt: e.target.value })}
              className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1"
            />
          </label>
          <button
            type="button"
            className="mt-2 text-rose-700 underline"
            onClick={handleDelete}
          >
            {t?.('admin.prospection.letterTemplate.image.delete') || 'Delete'}
          </button>
        </div>
      ) : null}
    </NodeViewWrapper>
  );
}

const PlaceholderWithView = LetterPlaceholderExtension.extend({
  addNodeView() {
    return ReactNodeViewRenderer(LetterPlaceholderNodeView);
  },
});

const QrBlockWithView = LetterQrBlockExtension.extend({
  addNodeView() {
    return ReactNodeViewRenderer(LetterQrBlockNodeView);
  },
});

const ImageWithView = LetterImageExtension.extend({
  addNodeView() {
    return ReactNodeViewRenderer(LetterImageNodeView);
  },
});

export default forwardRef(function LetterTemplateEditor({
  letterDocument,
  readOnly = false,
  t,
  onDocumentChange,
  onQrCountChange,
  onImageCountChange,
  qrInsertDisabled = false,
  imageInsertDisabled = false,
  onImageFileSelected,
  imageUploading = false,
  getImagePreviewUrl,
  retryImagePreview,
  showBilingualQrNote = false,
}, ref) {
  const letterDocumentRef = useRef(letterDocument);
  letterDocumentRef.current = letterDocument;

  const extensions = useMemo(
    () => [
      StarterKit.configure({
        code: false,
        codeBlock: false,
        heading: { levels: [1, 2, 3] },
        link: false,
        underline: false,
      }),
      LetterAlignmentExtension,
      LetterFontExtension,
      LetterFontSizeExtension,
      PlaceholderWithView,
      QrBlockWithView,
      ImageWithView,
    ],
    [],
  );

  const editor = useEditor({
    extensions,
    content: letterDocument,
    editable: !readOnly,
    onUpdate: ({ editor: ed }) => {
      const json = ed.getJSON();
      if (
        typeof onDocumentChange === 'function' &&
        !letterTemplateDocumentsEqual(json, letterDocumentRef.current)
      ) {
        onDocumentChange(json);
      }
      if (typeof onQrCountChange === 'function') {
        onQrCountChange(countLetterTemplateQrBlocks(json));
      }
      if (typeof onImageCountChange === 'function') {
        onImageCountChange(countLetterTemplateImageNodes(json));
      }
    },
    editorProps: {
      attributes: {
        class:
          'letter-template-editor min-h-[320px] rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm leading-relaxed text-slate-900 focus:outline-none ' +
          '[&_h1]:mb-4 [&_h1]:text-2xl [&_h1]:font-bold ' +
          '[&_h2]:mb-3 [&_h2]:text-xl [&_h2]:font-semibold ' +
          '[&_h3]:mb-2 [&_h3]:text-lg [&_h3]:font-semibold ' +
          '[&_p]:mb-3 ' +
          '[&_ul]:mb-3 [&_ul]:list-disc [&_ul]:space-y-1 [&_ul]:pl-5 ' +
          '[&_ol]:mb-3 [&_ol]:list-decimal [&_ol]:space-y-1 [&_ol]:pl-5 ' +
          '[&_blockquote]:mb-3 [&_blockquote]:border-l-4 [&_blockquote]:border-slate-300 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-slate-700 ' +
          '[&_hr]:my-4 [&_hr]:border-slate-300 ' +
          '[&_[data-letter-font="times"]]:font-serif ' +
          '[&_[data-letter-font="courier"]]:font-mono',
      },
    },
  });

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!readOnly);
  }, [editor, readOnly]);

  useEffect(() => {
    if (!editor || !letterDocument) return;
    const current = editor.getJSON();
    if (!letterTemplateDocumentsEqual(current, letterDocument)) {
      editor.commands.setContent(letterDocument, { emitUpdate: false });
    }
    if (typeof onQrCountChange === 'function') {
      onQrCountChange(countLetterTemplateQrBlocks(letterDocument));
    }
    if (typeof onImageCountChange === 'function') {
      onImageCountChange(countLetterTemplateImageNodes(letterDocument));
    }
  }, [editor, letterDocument, onQrCountChange, onImageCountChange]);

  useEffect(() => {
    if (!editor) return;
    editor.storage.letterQrLabel = t('admin.prospection.letterTemplate.qr.label');
    editor.storage.letterQrDeleteLabel = t('admin.prospection.letterTemplate.qr.delete');
    editor.storage.placeholderLabelFn = (key) => letterTemplatePlaceholderLabel(key, t);
    editor.storage.letterImageT = t;
    editor.storage.getImagePreviewUrl = getImagePreviewUrl;
    editor.storage.retryImagePreview = retryImagePreview;
    editor.storage.bilingualSharedQrNote = showBilingualQrNote;
    editor.storage.bilingualSharedQrNoteText = showBilingualQrNote
      ? t('admin.prospection.letterTemplate.qr.bilingualSharedNote')
      : '';
  }, [editor, t, getImagePreviewUrl, retryImagePreview, showBilingualQrNote]);

  const hasQrBlock = countLetterTemplateQrBlocks(editor?.getJSON?.()) > 0;

  const canInsertPlaceholder = () => {
    if (!editor) return false;
    const { $from } = editor.state.selection;
    for (let depth = $from.depth; depth > 0; depth -= 1) {
      const type = $from.node(depth).type.name;
      if (type === 'paragraph' || type === 'heading') return true;
    }
    return false;
  };

  const handleInsertPlaceholder = (key) => {
    if (!editor) return;
    if (!canInsertPlaceholder()) {
      editor.chain().focus().insertContent({ type: 'paragraph', content: [] }).run();
    }
    editor.chain().focus().insertLetterPlaceholder(key).run();
  };

  const handleInsertImage = (attrs) => {
    if (!editor) return;
    editor.chain().focus().insertLetterImage(attrs).run();
  };

  useImperativeHandle(
    ref,
    () => ({
      insertImage: handleInsertImage,
    }),
    [editor],
  );

  return (
    <div className="space-y-2">
      <LetterTemplateToolbar
        editor={editor}
        t={t}
        readOnly={readOnly}
        hasQrBlock={hasQrBlock}
        qrInsertDisabled={qrInsertDisabled}
        imageInsertDisabled={imageInsertDisabled}
        imageUploading={imageUploading}
        canInsertPlaceholder={canInsertPlaceholder()}
        onInsertPlaceholder={handleInsertPlaceholder}
        onImageFileSelected={onImageFileSelected}
      />
      <EditorContent editor={editor} />
    </div>
  );
});

export function getLetterTemplateEditorJson(editor) {
  return editor?.getJSON?.() ?? null;
}

export { LETTER_TEMPLATE_FONT_CSS };
